import { api } from "../api";

/**
 * Chat API Methods
 * Contains all the API calls needed for chat functionality
 */
export const chatAPI = {
  /**
   * Get recent chat conversations for the current user
   * @returns {Promise} Object with success status and data/error
   */
  getRecentChats: async () => {
    try {
      const response = await api.get("/chat");
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "Failed to get recent chats"
      };
    }
  },

  /**
   * Get chat messages between current user and another user
   * @param {string} userId - ID of the other user
   * @returns {Promise} Object with success status and data/error
   */
  getChatMessages: async (userId) => {
    try {
      if (!userId) {
        return {
          success: false,
          error: "Missing user ID"
        };
      }

      const response = await api.get(`/chat/${userId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "Failed to get chat messages"
      };
    }
  },

  /**
   * Search for users 
   * @param {string} term - Search term
   * @returns {Promise} Object with success status and data/error
   */
  uploadAttachment: async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post("/chat/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "Failed to upload file",
      };
    }
  },

  // Search conversations + message content (WhatsApp-style)
  searchChats: async (term) => {
    try {
      if (!term || term.length < 2) {
        return { success: true, conversations: [], messages: [] };
      }
      const response = await api.get(`/chat/search?term=${encodeURIComponent(term)}`);
      return response.data;
    } catch (error) {
      return { success: false, conversations: [], messages: [], error: error.response?.data?.error || "Search failed" };
    }
  },

  searchUsers: async (term) => {
    try {
      if (!term || term.length < 2) {
        return {
          success: false,
          error: "Search term must be at least 2 characters"
        };
      }

      const response = await api.get(`/users/search?term=${encodeURIComponent(term)}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "Failed to search users"
      };
    }
  },

  // ── Group API ──
  createGroup: async ({ name, description, members }) => {
    try {
      const response = await api.post("/groups", { name, description, members });
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || "Failed to create group" };
    }
  },

  listGroups: async () => {
    try {
      const response = await api.get("/groups");
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || "Failed to load groups" };
    }
  },

  getGroup: async (id) => {
    try {
      const response = await api.get(`/groups/${id}`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || "Failed to load group" };
    }
  },

  getGroupMessages: async (id) => {
    try {
      const response = await api.get(`/groups/${id}/messages`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || "Failed to load messages" };
    }
  },

  updateGroup: async (id, data) => {
    try {
      const response = await api.put(`/groups/${id}`, data);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || "Failed to update group" };
    }
  },

  addGroupMembers: async (id, members) => {
    try {
      const response = await api.post(`/groups/${id}/members`, { members });
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || "Failed to add members" };
    }
  },

  removeGroupMember: async (groupId, userId) => {
    try {
      const response = await api.delete(`/groups/${groupId}/members/${userId}`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || "Failed to remove member" };
    }
  },

  deleteGroup: async (id) => {
    try {
      await api.delete(`/groups/${id}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || "Failed to delete group" };
    }
  },
};

export default chatAPI; 